import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import CommonTable from '../../../../common/table/ComonTable';
import SectionHeaderBar from '../../../../common/pageheading/SectionHeaderBar';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import apiServices from '../../../../../services/apiService';
import SearchBar from '../../../../common/formfields/SearchBar';
import { toast } from 'react-toastify';
import DeleteModal from '../../../../common/commonmodal/DeleteModal';

const ParkingBookingList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [bookingList, setBookingList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });
    
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const fetchBookingList = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/ParkingSlotMaster/getParkingSlotOfAllUnit`
            });

            if (response.isSuccess || response.success || response.IsSuccess) {
                let data = response.data;
                if (!Array.isArray(data)) {
                    data = data ? [data] : [];
                }
                
                // If a user is logged in as a specific unit, we would filter here.
                // Assuming Admin view for now (shows all)
                const unitId = localStorage.getItem("unitId");
                if (unitId && unitId !== "undefined") {
                    data = data.filter(item => String(item.unitId) === String(unitId));
                }

                setBookingList(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching bookings:", error);
            toast.error("Failed to fetch parking bookings.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookingList();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const onPageChange = (page) => {
        setCurrentPage(page);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "SITE", key: "siteName" },
        { label: "WING", key: "wingName" },
        { label: "UNIT", key: "unitName" },
        { label: "FLOOR", key: "floorName" },
        { label: "OWNER NAME", key: "ownerName" },
        { label: "VEHICLE TYPE", key: "vehicleType" },
        { label: "PARKING TYPE", key: "parkingType" },
        { label: "SLOT NAME", key: "slotName" },
        { label: "STATUS", key: "isActive" }
    ];

    const renderCell = (key, row) => {
        switch (key) {
            case "no":
            case "slotName":
                return <span className="text-xs font-semibold text-[#1E293B]">{row[key] || "—"}</span>;
            case "siteName":
            case "wingName":
            case "unitName":
            case "floorName":
            case "ownerName":
            case "vehicleType":
            case "parkingType":
                return <span className="text-xs font-medium text-slate-700">{row[key] || "—"}</span>;
            case "isActive":
                let statusColor = "bg-gray-100 text-gray-600";
                if (row.isActive === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (row.isActive === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {row.isActive || "—"}
                    </span>
                );
            default:
                return <span className="text-xs font-normal text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const headerAction = (
        <div className="flex flex-wrap items-center gap-3">
            <SearchBar
                onSearch={setSearchQuery}
                placeholder="Search booking..."
                className="w-48"
            />
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-[#0F172A] focus:outline-none focus:ring-1 focus:border-gray-400"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
            <button
                onClick={() => navigate('/zentcomm/parking-management/parking-Booking/form')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold flex items-center gap-2 hover:bg-indigo-700 transition-all shadow-sm"
            >
                <FontAwesomeIcon icon={faPlus} />
                Add New Booking
            </button>
        </div>
    );

    // Filter by search query and status tab
    const safeData = Array.isArray(bookingList) ? bookingList : [];
    const filteredData = safeData.filter(item => {
        let matchesSearch = true;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            matchesSearch = (
                (item.ownerName || "").toLowerCase().includes(query) ||
                (item.unitName || "").toLowerCase().includes(query) ||
                (item.slotName || "").toLowerCase().includes(query)
            );
        }
        
        let matchesStatus = true;
        if (statusFilter) {
            matchesStatus = item.isActive === statusFilter;
        }

        return matchesSearch && matchesStatus;
    });

    const paginatedData = filteredData.slice((currentPage - 1) * pageInfo.pageSize, currentPage * pageInfo.pageSize).map((item, idx) => ({
        ...item,
        id: item.parkingSlotId || `bk-${idx}`,
        no: (currentPage - 1) * pageInfo.pageSize + idx + 1
    }));

    const handleDeleteClick = (row) => {
        setRecordToDelete(row);
        setIsDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!recordToDelete) return;
        try {
            const res = await apiServices({
                method: "DELETE",
                url: `/ParkingBookingDetails/DeleteParkingBookingDetails/${recordToDelete.parkingSlotId}`
            });
            if (res.isSuccess || res.success || res.IsSuccess) {
                toast.success("Parking slot released successfully");
                fetchBookingList();
            } else {
                toast.error("Failed while release parking slot");
            }
        } catch (error) {
            toast.error("Error releasing parking slot");
        } finally {
            setIsDeleteOpen(false);
            setRecordToDelete(null);
        }
    };

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6 min-h-screen bg-[#FFFFFF]">
            <div className='flex flex-col gap-1'>
                <Breadcrumbs />
                <SectionHeaderBar
                    heading="Parking Reservation List"
                    subHeading="Manage parking slot bookings and reservations"
                />
            </div>



            <CommonTable
                tableHeading="Parking Reservations"
                data={paginatedData}
                headers={tableHeaders}
                renderCell={renderCell}
                headerAction={headerAction}
                pagination={{
                    ...pageInfo,
                    pageNumber: currentPage,
                    totalRecords: filteredData.length,
                    totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                }}
                onPageChange={onPageChange}
                onLimitChange={handleLimitChange}
                loading={loading}
                onDelete={handleDeleteClick}
                hideEdit={true}
                deleteIcon="fa-trash"
            />

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                onDelete={confirmDelete}
                title="Release Parking Slot"
                message={`Are you sure you want to release this ${recordToDelete?.slotName} parking slot?`}
            />
        </div>
    );
};

export default ParkingBookingList;
